import { type FormEvent, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/ui/Toast";

// Con mouse (escritorio) el cursor ya espera en «Correo»; en el celular no, porque
// abriría el teclado sin que nadie lo pida.
const conMouse =
  typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

export default function Login() {
  const user = useAuth((s) => s.user);
  const login = useAuth((s) => s.login);
  const nav = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Sesión iniciada", `Bienvenido de nuevo`);
      nav("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
        toast.error("No pudimos entrar", err.detail);
      } else {
        setError("Error de conexión");
        toast.error("Error de conexión", "Verifica tu red");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm card space-y-4">
        <div className="flex flex-col items-center gap-3 -mt-2">
          <img
            src="/emprenet-logo.png"
            alt="Emprenet"
            width={239}
            height={64}
            className="h-10 w-auto"
            onError={(e) => {
              // Si el logo aun no esta en el repo, escondemos la imagen
              // y dejamos solo el texto debajo. El form sigue siendo usable.
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          {/* Sin botón «Ayuda» a propósito (Joe, 2026-10-21): la pantalla de entrada no lleva. */}
          <div className="w-full text-center">
            <h1 className="font-mono text-accent-400 text-lg" translate="no">
              ArkTickets
            </h1>
            <p className="text-zinc-400 text-sm mt-1">Mesa de ayuda</p>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="bg-red-950/40 border border-red-800 text-red-300 text-sm rounded px-3 py-2"
          >
            {error}
          </div>
        )}

        <label className="block">
          <span className="text-sm text-zinc-300">Correo</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            spellCheck={false}
            required
            autoFocus={conMouse}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@arknex.net…"
            className="input mt-1"
          />
        </label>

        <label className="block">
          <span className="text-sm text-zinc-300">Contraseña</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input mt-1"
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="btn-primary w-full disabled:opacity-50"
        >
          {submitting ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </div>
  );
}
